import { useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Plus, Trash2, Package, Send, CheckCircle } from "lucide-react";
import { toast } from "react-toastify";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIZES = ["XS", "S", "M", "L", "XL", "2XL", "3XL"];

export default function CreateOrder({ onCreated }) {
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    payment_status: "unpaid", due_date: "", notes: "", source: "manual",
  });
  const [items, setItems] = useState([newItem()]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [createdOrder, setCreatedOrder] = useState(null);
  const [sendingInvoice, setSendingInvoice] = useState(false);

  function newItem() {
    return { name: "", quantity: 1, sizes: {}, colors: [], print_locations: [], unit_price: 0, notes: "" };
  }

  const addItem = () => setItems([...items, newItem()]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const updateItem = (idx, field, value) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: value };
    setItems(updated);
  };

  const updateSize = (idx, size, qty) => {
    const updated = [...items];
    const sizes = { ...updated[idx].sizes };
    if (qty > 0) sizes[size] = qty;
    else delete sizes[size];
    updated[idx] = { ...updated[idx], sizes };
    // Auto-calc total quantity from sizes
    const totalQty = Object.values(sizes).reduce((s, v) => s + v, 0);
    if (totalQty > 0) updated[idx].quantity = totalQty;
    setItems(updated);
  };

  const toggleColor = (idx, color) => {
    const updated = [...items];
    const colors = updated[idx].colors.includes(color)
      ? updated[idx].colors.filter((c) => c !== color)
      : [...updated[idx].colors, color];
    updated[idx] = { ...updated[idx], colors };
    setItems(updated);
  };

  const toggleLocation = (idx, loc) => {
    const updated = [...items];
    const locs = updated[idx].print_locations.includes(loc)
      ? updated[idx].print_locations.filter((l) => l !== loc)
      : [...updated[idx].print_locations, loc];
    updated[idx] = { ...updated[idx], print_locations: locs };
    setItems(updated);
  };

  const getTotal = () => items.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.customer_name || !form.customer_email) {
      setError("Customer name and email required");
      return;
    }
    if (items.some((i) => !i.name)) {
      setError("All items need a name");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/oms/orders`, {
        ...form,
        items,
        total_amount: getTotal(),
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
      }, { withCredentials: true });
      
      if (form.payment_status !== "paid") {
        setCreatedOrder(data);
        toast.success("Order created! Send an invoice to the customer.");
      } else {
        onCreated(data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create order");
    } finally {
      setSubmitting(false);
    }
  };

  const sendInvoice = async () => {
    if (!createdOrder) return;
    setSendingInvoice(true);
    try {
      const { data } = await axios.post(`${API}/oms/orders/${createdOrder.id}/send-invoice`, {}, { withCredentials: true });
      toast.success(data.message);
      onCreated(createdOrder);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invoice");
    } finally { setSendingInvoice(false); }
  };

  const COLORS = ["Black", "White", "Navy", "Red", "Royal", "Gray", "Forest Green", "Maroon"];
  const LOCATIONS = ["front", "back", "left_chest", "right_chest", "left_sleeve", "right_sleeve"];

  // ── Invoice prompt after creating unpaid order ──
  if (createdOrder) {
    return (
      <div className="space-y-4" data-testid="invoice-prompt">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-6 sm:p-8 text-center space-y-4">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-white">Order Created!</h2>
            <p className="text-zinc-400">
              Order <span className="text-orange-400 font-bold">{createdOrder.order_number}</span> for{" "}
              <span className="text-white">{createdOrder.customer_name}</span> —{" "}
              <span className="text-green-400 font-bold">${createdOrder.total_amount?.toFixed(2)}</span>
            </p>
            <p className="text-zinc-500 text-sm">Payment status: <Badge className="bg-yellow-500/20 text-yellow-400">{createdOrder.payment_status}</Badge></p>

            <div className="grid sm:grid-cols-2 gap-3 max-w-md mx-auto pt-2">
              <Button onClick={sendInvoice} disabled={sendingInvoice} className="bg-gradient-to-r from-orange-500 to-red-600 text-white" data-testid="send-invoice-btn">
                <Send className="w-4 h-4 mr-2" /> {sendingInvoice ? "Sending..." : "Send Payable Invoice"}
              </Button>
              <Button onClick={() => onCreated(createdOrder)} variant="outline" className="border-zinc-700 text-zinc-300" data-testid="skip-invoice-btn">
                Skip &mdash; View Order
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-testid="create-order-form" className="space-y-6">
      <h2 className="text-2xl font-bold text-white">New Order</h2>

      {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm">{error}</div>}

      {/* Customer */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-2"><CardTitle className="text-white text-base">Customer Info</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-zinc-400 text-xs">Name *</Label>
            <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" required data-testid="customer-name" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">Email *</Label>
            <Input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" required data-testid="customer-email" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">Phone</Label>
            <Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" data-testid="customer-phone" />
          </div>
        </CardContent>
      </Card>

      {/* Items */}
      {items.map((item, idx) => (
        <Card key={idx} className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Package className="w-4 h-4 text-orange-400" /> Item {idx + 1}
            </CardTitle>
            {items.length > 1 && (
              <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-300">
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-zinc-400 text-xs">Item Name *</Label>
                <Input value={item.name} onChange={(e) => updateItem(idx, "name", e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" placeholder="Custom T-Shirts" data-testid={`item-name-${idx}`} />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Quantity</Label>
                <Input type="number" min={1} value={item.quantity} onChange={(e) => updateItem(idx, "quantity", parseInt(e.target.value) || 1)} className="bg-zinc-800 border-zinc-700 text-white" data-testid={`item-qty-${idx}`} />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Unit Price ($)</Label>
                <Input type="number" step="0.01" min={0} value={item.unit_price} onChange={(e) => updateItem(idx, "unit_price", parseFloat(e.target.value) || 0)} className="bg-zinc-800 border-zinc-700 text-white" data-testid={`item-price-${idx}`} />
              </div>
            </div>

            {/* Sizes */}
            <div>
              <Label className="text-zinc-400 text-xs mb-1">Size Breakdown</Label>
              <div className="flex flex-wrap gap-2">
                {SIZES.map((size) => (
                  <div key={size} className="flex items-center gap-1">
                    <span className="text-xs text-zinc-500 w-8">{size}</span>
                    <Input
                      type="number"
                      min={0}
                      value={item.sizes[size] || ""}
                      onChange={(e) => updateSize(idx, size, parseInt(e.target.value) || 0)}
                      className="w-16 bg-zinc-800 border-zinc-700 text-white text-sm h-8"
                      placeholder="0"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Colors */}
            <div>
              <Label className="text-zinc-400 text-xs mb-1">Colors</Label>
              <div className="flex flex-wrap gap-1">
                {COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => toggleColor(idx, color)}
                    className={`text-xs px-2 py-1 rounded-full transition ${item.colors.includes(color) ? "bg-orange-500/20 text-orange-400 border border-orange-500/30" : "bg-zinc-800 text-zinc-500 border border-zinc-700"}`}
                  >
                    {color}
                  </button>
                ))}
              </div>
            </div>

            {/* Print Locations */}
            <div>
              <Label className="text-zinc-400 text-xs mb-1">Print Locations</Label>
              <div className="flex flex-wrap gap-1">
                {LOCATIONS.map((loc) => (
                  <button
                    key={loc}
                    type="button"
                    onClick={() => toggleLocation(idx, loc)}
                    className={`text-xs px-2 py-1 rounded-full capitalize transition ${item.print_locations.includes(loc) ? "bg-blue-500/20 text-blue-400 border border-blue-500/30" : "bg-zinc-800 text-zinc-500 border border-zinc-700"}`}
                  >
                    {loc.replace(/_/g, " ")}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-zinc-400 text-xs">Item Notes</Label>
              <Input value={item.notes} onChange={(e) => updateItem(idx, "notes", e.target.value)} className="bg-zinc-800 border-zinc-700 text-white text-sm" placeholder="Special instructions..." />
            </div>
          </CardContent>
        </Card>
      ))}

      <Button type="button" variant="outline" onClick={addItem} className="border-zinc-700 text-zinc-300 border-dashed w-full" data-testid="add-item-btn">
        <Plus className="w-4 h-4 mr-2" /> Add Item
      </Button>

      {/* Order settings */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="py-4 grid sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-zinc-400 text-xs">Payment Status</Label>
            <select value={form.payment_status} onChange={(e) => setForm({ ...form, payment_status: e.target.value })} className="w-full bg-zinc-800 border border-zinc-700 text-zinc-300 rounded-lg px-3 py-2 text-sm mt-1" data-testid="payment-select">
              <option value="unpaid">Unpaid</option>
              <option value="partial">Partial</option>
              <option value="paid">Paid</option>
            </select>
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">Due Date</Label>
            <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="due-date" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">Source</Label>
            <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="w-full bg-zinc-800 border border-zinc-700 text-zinc-300 rounded-lg px-3 py-2 text-sm mt-1">
              <option value="manual">Manual</option>
              <option value="website">Website</option>
              <option value="phone">Phone</option>
              <option value="email">Email</option>
            </select>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="py-4">
          <Label className="text-zinc-400 text-xs">Order Notes</Label>
          <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1" rows={3} placeholder="Any special notes..." data-testid="order-notes" />
        </CardContent>
      </Card>

      <div className="flex items-center justify-between bg-zinc-900 border border-zinc-800 rounded-xl p-4">
        <div>
          <span className="text-zinc-400 text-sm">Total</span>
          <div className="text-3xl font-bold text-white">${getTotal().toFixed(2)}</div>
        </div>
        <Button type="submit" disabled={submitting} className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-8" data-testid="create-order-btn">
          {submitting ? "Creating..." : "Create Order"}
        </Button>
      </div>
    </form>
  );
}
